import { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import UserForm from './UserForm'
import UserProfile from './UserProfile'
import LicenseAdministrationContent from './LicenseAdministrationContent'
import DeviceAdministrationContent from './DeviceAdministrationContent'
import SoftwareProductAdministrationContent from './SoftwareProductAdministrationContent'
import DownloadAdministrationContent from './DownloadAdministrationContent'
import AccessTokens from './AccessTokens'
import AppUsageChart from './AppUsageChart'
import { apiFetch } from '../utils/api'
import { validatePasswordComplexity } from '../utils/validation'
import { Chart as ChartJS, CategoryScale, LinearScale, ArcElement, BarElement, Title, Tooltip, Legend } from 'chart.js'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import { Pie } from 'react-chartjs-2'
import DeviceModelChart from './DeviceModelChart'
import PieChartCard from './PieChartCard'
import {
  BarChart3,
  TrendingUp,
  Monitor,
  Package,
  Shield,
  Users,
  User,
  ChevronDown,
  ChevronUp,
  Settings,
  LogOut,
  Download
} from 'lucide-react'

ChartJS.register(CategoryScale, LinearScale, ArcElement, BarElement, Title, Tooltip, Legend, ChartDataLabels)

const AdminDashboard = ({ user, onLogout }) => {
  const [activeMenuItem, setActiveMenuItem] = useState('dashboard')
  const [dashboardStats, setDashboardStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    pendingUsers: 0
  })
  const [recentActivities, setRecentActivities] = useState([])
  const [userDropdownOpen, setUserDropdownOpen] = useState(false)
  const [appVersion, setAppVersion] = useState('...')
  const [copyright, setCopyright] = useState('')

  const handleMenuClick = (menuItem) => {
    setActiveMenuItem(menuItem)
  }

  const toggleUserDropdown = () => {
    setUserDropdownOpen(!userDropdownOpen)
  }

  const handleProfileClick = () => {
    setActiveMenuItem('profile')
    setUserDropdownOpen(false)
  }

  const handleAccessTokensClick = () => {
    setActiveMenuItem('access-tokens')
    setUserDropdownOpen(false)
  }

  const handleLogoutClick = () => {
    setUserDropdownOpen(false)
    onLogout()
  }

  // Load dashboard statistics
  const loadDashboardStats = async () => {
    try {
      const response = await apiFetch('/api/admin/users')

      if (response.ok) {
        const data = await response.json()
        const users = data.users || []
        const activeUsers = users.filter(u => u.is_active).length
        const pendingUsers = users.filter(u => !u.is_active).length
        
        setDashboardStats({
          totalUsers: users.length,
          activeUsers,
          pendingUsers
        })
      }
    } catch (err) {
      console.error('Error loading dashboard stats:', err)
    }
  }

  // Load recent activities
  const loadRecentActivities = async () => {
    try {
      const response = await apiFetch('/api/admin/activities')

      if (response.ok) {
        const data = await response.json()
        setRecentActivities(data.activities || [])
      }
    } catch (err) {
      console.error('Error loading recent activities:', err)
    }
  }

  // Format timestamp for display
  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp)
    const now = new Date()
    const diffInMinutes = Math.floor((now - date) / (1000 * 60))
    
    if (diffInMinutes < 1) return 'Just now'
    if (diffInMinutes < 60) return `${diffInMinutes} minutes ago`
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)} hours ago`
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString()
  }

  // Load stats and activities on component mount
  useEffect(() => {
    void loadDashboardStats()
    void loadRecentActivities()
  }, [])

  // Handle click outside dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userDropdownOpen && !event.target.closest('.user-dropdown')) {
        setUserDropdownOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [userDropdownOpen])

  // Refresh all dashboard data when returning to dashboard
  useEffect(() => {
    if (activeMenuItem === 'dashboard') {
      void loadDashboardStats();
      void loadRecentActivities();
    }
  }, [activeMenuItem])

  // Load application version and copyright from backend
  useEffect(() => {
    const loadVersionInfo = async () => {
      try {
        const response = await apiFetch('/version')
        if (response.ok) {
          const data = await response.json()
          setAppVersion(data.version || '1.0.0')
          setCopyright(data.copyright || 'Copyright by Rolf Warnecke')
        }
      } catch (err) {
        console.error('Error loading version info:', err)
        setAppVersion('1.0.0') // Fallback version
        setCopyright('Copyright by Rolf Warnecke') // Fallback copyright
      }
    }
    void loadVersionInfo()
  }, [])

  const renderContent = () => {
    switch (activeMenuItem) {
      case 'dashboard':
        return <DashboardContent stats={dashboardStats} activities={recentActivities} formatTimestamp={formatTimestamp} />
      case 'licenses':
        return <LicenseAdministrationContent />
      case 'users':
        return <UserAdministrationContent currentUser={user} />
      case 'devices':
        return <DeviceAdministrationContent />
      case 'products':
        return <SoftwareProductAdministrationContent />
      case 'downloads':
        return <DownloadAdministrationContent />
      case 'analytics':
        return <AnalyticsContent />
      case 'profile':
        return <UserProfile user={user} onBack={() => setActiveMenuItem('dashboard')} />
      case 'access-tokens':
        return <AccessTokens />
      default:
        return <DashboardContent stats={dashboardStats} activities={recentActivities} formatTimestamp={formatTimestamp} />
    }
  }

  return (
    <div className="admin-layout">
      {/* Top Navigation Bar */}
      <nav className="top-navbar">
        <div className="navbar-brand">
          <h1>CodeNexus Admin</h1>
        </div>
        <div className="navbar-user">
          <div className="user-dropdown">
            <button className="user-dropdown-btn" onClick={toggleUserDropdown}>
              <span className="user-avatar">
                <User size={16} />
              </span>
              <span className="user-name">Welcome, {user.first_name} {user.last_name}</span>
              <span className="dropdown-arrow">
                {userDropdownOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
              </span>
            </button>
            {userDropdownOpen && (
              <div className="user-dropdown-menu">
                <button className="dropdown-item" onClick={handleProfileClick}>
                  <Settings size={14} style={{marginRight: '8px'}} />
                  Profile
                </button>
                <button className="dropdown-item" onClick={handleAccessTokensClick}>
                  <Shield size={14} style={{marginRight: '8px'}} />
                  Access Tokens
                </button>
                <div className="dropdown-divider"></div>
                <button className="dropdown-item logout" onClick={handleLogoutClick}>
                  <LogOut size={14} style={{marginRight: '8px'}} />
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </nav>

      <div className="admin-content">
        {/* Sidebar */}
        <aside className="sidebar">
          <ul className="sidebar-menu">
            <li>
              <button type="button" className={`menu-item ${activeMenuItem === 'dashboard' ? 'active' : ''}`} onClick={() => handleMenuClick('dashboard')}>
                <BarChart3 size={18} className="menu-icon" />
                <span className="menu-text">Dashboard</span>
              </button>
            </li>
            <li>
              <button type="button" className={`menu-item ${activeMenuItem === 'analytics' ? 'active' : ''}`} onClick={() => handleMenuClick('analytics')}>
                <TrendingUp size={18} className="menu-icon" />
                <span className="menu-text">Analytics</span>
              </button>
            </li>
            <li>
              <button type="button" className={`menu-item ${activeMenuItem === 'devices' ? 'active' : ''}`} onClick={() => handleMenuClick('devices')}>
                <Monitor size={18} className="menu-icon" />
                <span className="menu-text">Devices</span>
              </button>
            </li>
            <li>
              <button type="button" className={`menu-item ${activeMenuItem === 'products' ? 'active' : ''}`} onClick={() => handleMenuClick('products')}>
                <Package size={18} className="menu-icon" />
                <span className="menu-text">Software Products</span>
              </button>
            </li>
            <li>
              <button type="button" className={`menu-item ${activeMenuItem === 'licenses' ? 'active' : ''}`} onClick={() => handleMenuClick('licenses')}>
                <Shield size={18} className="menu-icon" />
                <span className="menu-text">Licenses</span>
              </button>
            </li>
            <li>
              <button type="button" className={`menu-item ${activeMenuItem === 'downloads' ? 'active' : ''}`} onClick={() => handleMenuClick('downloads')}>
                <Download size={18} className="menu-icon" />
                <span className="menu-text">Downloads</span>
              </button>
            </li>
            <li>
              <button type="button" className={`menu-item ${activeMenuItem === 'users' ? 'active' : ''}`} onClick={() => handleMenuClick('users')}>
                <Users size={18} className="menu-icon" />
                <span className="menu-text">User Administration</span>
              </button>
            </li>
          </ul>

          {/* Sidebar Footer with Version */}
          <div style={{
            padding: '20px',
            marginTop: 'auto',
            textAlign: 'center',
            borderTop: '1px solid #374151',
            fontSize: '0.75rem',
            color: '#9ca3af'
          }}>
            <div style={{ marginBottom: '4px' }}>{copyright}</div>
            <div style={{ fontWeight: '500' }}>v{appVersion}</div>
          </div>
        </aside>

        {/* Main Content Area */}
        <main className="main-content">
          {renderContent()}
        </main>
      </div>
    </div>
  )
}

// Dashboard Content Component
const DashboardContent = ({ stats, activities, formatTimestamp }) => {
  const [showAll, setShowAll] = useState(false)
  const [allActivities, setAllActivities] = useState([])
  const [errorAll, setErrorAll] = useState('')
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [pageSize, setPageSize] = useState(25)
  const [loadingAll, setLoadingAll] = useState(false)

  // State for license stats
  const [licenseStats, setLicenseStats] = useState({
    lifetime: 0,
    abo: 0,
    aboExpired: 0,
    trial: 0,
    trialExpired: 0,
  })
  const [loadingLicenses, setLoadingLicenses] = useState(true)
  const [licenseError, setLicenseError] = useState('')

  // State for device counts
  const [deviceCounts, setDeviceCounts] = useState({
    mobile_devices: 0,
    desktop_devices: 0,
    total_devices: 0
  })
  const [loadingDevices, setLoadingDevices] = useState(true)
  const [deviceError, setDeviceError] = useState('')


  const fetchAllActivities = async (pageToFetch = 1) => {
    setLoadingAll(true)
    try {
      const response = await apiFetch(`/api/admin/activities/all?page=${pageToFetch}&page_size=${pageSize}`)
      const data = await response.json()
      if (response.ok) {
        setAllActivities(data.activities || [])
        setTotal(data.total || 0)
        setPage(data.page || 1)
        setPageSize(data.page_size || 25)
      } else {
        setErrorAll(data.error || 'Failed to load all activities')
      }
    } catch (err) {
      setErrorAll('Network error while loading activities')
      console.error('Error fetching all activities:', err)
    } finally {
      setLoadingAll(false)
    }
  }

  const handleShowAll = () => {
    void fetchAllActivities(1)
    setShowAll(true)
  }

  const handleHideAll = () => {
    setShowAll(false)
    setErrorAll('')
    setAllActivities([])
    setPage(1)
    setTotal(0)
  }

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > Math.ceil(total / pageSize)) return
    void fetchAllActivities(newPage)
  }

  useEffect(() => {
    // Load device counts for dashboard stats
    const fetchDeviceCounts = async () => {
      setLoadingDevices(true)
      setDeviceError('')
      try {
        const res = await apiFetch('/api/admin/device-counts')
        const data = await res.json()
        if (res.ok) {
          setDeviceCounts(data)
        } else {
          setDeviceError(data.error || 'Failed to load device counts')
        }
      } catch {
        setDeviceError('Network error while loading device counts')
      } finally {
        setLoadingDevices(false)
      }
    }

    // Load all licenses for dashboard stats
    const fetchLicenses = async () => {
      setLoadingLicenses(true)
      setLicenseError('')
      try {
        const res = await apiFetch('/api/admin/licenses')
        const data = await res.json()
        if (res.ok) {
          const licenses = data.licenses || []
          // Only count licenses for verified users and valid product plans
          let lifetime = 0, abo = 0, aboExpired = 0, trial = 0, trialExpired = 0
          const now = new Date()
          licenses.forEach(l => {
            // Log all relevant fields for debugging
            console.log('License:', {
              id: l.id,
              userId: l.userId,
              user: l.user,
              productPlan: l.productPlan,
              billing_interval: l.productPlan?.billing_interval,
              status: l.status
            })
            if (!l.user?.is_active) return
            if (!l.productPlan?.billing_interval) return
            const interval = l.productPlan.billing_interval.toLowerCase()
            // Accept multiple possible values for lifetime
            const isLifetime = [
              'one-time', 'lifetime', 'lifetime-license', 'lifetime_license', 'lifetime license'
            ].includes(interval)
            if (isLifetime) {
              lifetime++
            } else if (interval === 'monthly' || interval === 'yearly') {
              if (l.status?.toLowerCase() === 'active' && (!l.endDate || new Date(l.endDate) > now)) {
                abo++
              } else {
                aboExpired++
              }
            } else if (interval === 'trial') {
              if (l.status?.toLowerCase() === 'active' && (!l.endDate || new Date(l.endDate) > now)) {
                trial++
              } else {
                trialExpired++
              }
            }
          })
          setLicenseStats({ lifetime, abo, aboExpired, trial, trialExpired })
          console.log('All licenses:', licenses);
        } else {
          setLicenseError(data.error || 'Failed to load licenses')
        }
      } catch {
        setLicenseError('Network error while loading licenses')
      } finally {
        setLoadingLicenses(false)
      }
    }

    void fetchDeviceCounts()
    void fetchLicenses()
  }, [])

  if (showAll) {
    const totalPages = Math.ceil(total / pageSize)
    return (
      <div className="content-section">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2>All Activities</h2>
          <button className="btn-primary" onClick={handleHideAll}>← Back</button>
        </div>
        {errorAll ? (
          <div className="error-message">{errorAll}</div>
        ) : (
          <>
            <div className="activities-table">
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ borderBottom: '2px solid #ccc', padding: '8px', textAlign: 'left' }}>Date/Time</th>
                    <th style={{ borderBottom: '2px solid #ccc', padding: '8px', textAlign: 'left' }}>User</th>
                    <th style={{ borderBottom: '2px solid #ccc', padding: '8px', textAlign: 'left' }}>Part</th>
                    <th style={{ borderBottom: '2px solid #ccc', padding: '8px', textAlign: 'left' }}>Level</th>
                    <th style={{ borderBottom: '2px solid #ccc', padding: '8px', textAlign: 'left' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    if (loadingAll) {
                      return <tr><td colSpan="5" style={{ textAlign: 'center', color: '#9ca3af', padding: '8px' }}>Loading...</td></tr>
                    }
                    if (allActivities.length === 0) {
                      return (
                        <tr>
                          <td colSpan="5" style={{ textAlign: 'center', color: '#9ca3af', padding: '8px' }}>
                            No activities found
                          </td>
                        </tr>
                      )
                    }
                    return allActivities.map(act => (
                      <tr key={act.id}>
                        <td style={{ padding: '8px' }}>{formatTimestamp(act.created_at)}</td>
                        <td style={{ padding: '8px' }}>{act.user ? act.user.email : 'System'}</td>
                        <td style={{ padding: '8px' }}>{act.part || '-'}</td>
                        <td style={{ padding: '8px' }}>{act.level || '-'}</td>
                        <td style={{ padding: '8px' }}>{act.description}</td>
                      </tr>
                    ))
                  })()}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: 12, gap: 8 }}>
                <button
                  className="btn-primary"
                  onClick={() => handlePageChange(1)}
                  disabled={page === 1}
                  title="First page"
                  style={{ padding: '8px 16px', fontSize: '15px', minWidth: '48px' }}
                >
                  &#124;&lt;
                </button>
                <button
                  className="btn-primary"
                  onClick={() => handlePageChange(page - 1)}
                  disabled={page === 1}
                  style={{ padding: '8px 16px', fontSize: '15px', minWidth: '48px' }}
                >
                  &lt;
                </button>
                {(() => {
                  const pageButtons = []
                  const maxVisible = 5
                  let startPage = Math.max(1, page - Math.floor(maxVisible / 2))
                  let endPage = Math.min(totalPages, startPage + maxVisible - 1)

                  if (endPage - startPage < maxVisible - 1) {
                    startPage = Math.max(1, endPage - maxVisible + 1)
                  }

                  if (startPage > 1) {
                    pageButtons.push(
                      <button
                        key={1}
                        className="btn-primary"
                        style={{ padding: '8px 16px', fontSize: '15px', minWidth: '48px' }}
                        onClick={() => handlePageChange(1)}
                      >
                        1
                      </button>
                    )
                    if (startPage > 2) {
                      pageButtons.push(<span key="dots-start" style={{ padding: '0 8px', color: '#666', fontSize: '15px' }}>...</span>)
                    }
                  }

                  for (let i = startPage; i <= endPage; i++) {
                    pageButtons.push(
                      <button
                        key={i}
                        className={`btn-primary${page === i ? ' active-page' : ''}`}
                        style={page === i ? {
                          background: '#fff',
                          color: '#4f46e5',
                          border: '2px solid #4f46e5',
                          fontWeight: 'bold',
                          padding: '8px 16px',
                          fontSize: '15px',
                          minWidth: '48px'
                        } : {
                          padding: '8px 16px',
                          fontSize: '15px',
                          minWidth: '48px'
                        }}
                        onClick={() => handlePageChange(i)}
                        disabled={page === i}
                      >
                        {i}
                      </button>
                    )
                  }

                  if (endPage < totalPages) {
                    if (endPage < totalPages - 1) {
                      pageButtons.push(<span key="dots-end" style={{ padding: '0 8px', color: '#666', fontSize: '15px' }}>...</span>)
                    }
                    pageButtons.push(
                      <button
                        key={totalPages}
                        className="btn-primary"
                        style={{ padding: '8px 16px', fontSize: '15px', minWidth: '48px' }}
                        onClick={() => handlePageChange(totalPages)}
                      >
                        {totalPages}
                      </button>
                    )
                  }

                  return pageButtons
                })()}
                <button
                  className="btn-primary"
                  onClick={() => handlePageChange(page + 1)}
                  disabled={page === totalPages}
                  style={{ padding: '8px 16px', fontSize: '15px', minWidth: '48px' }}
                >
                  &gt;
                </button>
                <button
                  className="btn-primary"
                  onClick={() => handlePageChange(totalPages)}
                  disabled={page === totalPages}
                  title="Last page"
                  style={{ padding: '8px 16px', fontSize: '15px', minWidth: '48px' }}
                >
                  &gt;&#124;
                </button>
              </div>
            )}
          </>
        )}
      </div>
    )
  }

  return (
    <div className="content-section">
      <h2>Dashboard</h2>
      {/* License/Subscription/Trial Stats */}
      <div className="dashboard-cards">
        <div className="dashboard-cards-row">
          <div className="dashboard-card">
            <h3>Total Users</h3>
            <div className="card-value">{stats.totalUsers}</div>
            <div className="card-subtitle">Registered users</div>
          </div>
          <div className="dashboard-card">
            <h3>Active Users</h3>
            <div className="card-value">{stats.activeUsers}</div>
            <div className="card-subtitle">Verified and active</div>
          </div>
          <div className="dashboard-card">
            <h3>Pending Verifications</h3>
            <div className="card-value">{stats.pendingUsers}</div>
            <div className="card-subtitle">Awaiting verification</div>
          </div>
        </div>
        <div className="dashboard-cards-row">
          <div className="dashboard-card">
            <h3>LifeTime Licenses</h3>
            <div className="card-value">{loadingLicenses ? '...' : licenseStats.lifetime}</div>
            <div className="card-subtitle">Activated (verified users)</div>
          </div>
          <div className="dashboard-card">
            <h3>Abos</h3>
            <div className="card-value">
              {loadingLicenses ? '...'
                : <>
                    {licenseStats.abo} / <span className="card-value card-red">{licenseStats.aboExpired}</span>
                  </>
              }
            </div>
            <div className="card-subtitle">Active / <span style={{ color: '#ef4444' }}>Expired</span> (verified users)</div>
          </div>
          <div className="dashboard-card">
            <h3>Trials</h3>
            <div className="card-value">
              {loadingLicenses ? '...'
                : <>
                    {licenseStats.trial} / <span className="card-value card-red">{licenseStats.trialExpired}</span>
                  </>
              }
            </div>
            <div className="card-subtitle">Active / <span style={{ color: '#ef4444' }}>Expired</span> (verified users)</div>
          </div>
        </div>
      </div>
      {/* License error display */}
      {licenseError && <div className="error-message">{licenseError}</div>}

      {/* System and Device Status Row */}
      <div className="dashboard-cards">
        <div className="dashboard-cards-row">
          <div className="dashboard-card">
            <h3>System Status</h3>
            <div className="card-value status-online">Online</div>
            <div className="card-subtitle">All systems operational</div>
          </div>
          <div className="dashboard-card">
            <h3>Registered Desktop Devices</h3>
            <div className="card-value">{loadingDevices ? '...' : deviceCounts.desktop_devices}</div>
            <div className="card-subtitle">Total desktop devices</div>
          </div>
          <div className="dashboard-card">
            <h3>Registered Mobile Devices</h3>
            <div className="card-value">{loadingDevices ? '...' : deviceCounts.mobile_devices}</div>
            <div className="card-subtitle">Total mobile devices</div>
          </div>
        </div>
      </div>
      {/* Device error display */}
      {deviceError && <div className="error-message">{deviceError}</div>}

      {/* App Usage Chart */}
      <AppUsageChart />


      {/* Activity log remains unchanged */}
      <div className="recent-activity">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3>Recent Activity</h3>
          <button className="btn-primary" onClick={handleShowAll} title="Show all activities">📋</button>
        </div>
        <div className="activity-list">
          {activities.length === 0 ? (
            <div className="activity-item">
              <span className="activity-text" style={{ color: '#9ca3af', fontStyle: 'italic' }}>
                No recent activities
              </span>
            </div>
          ) : (
            activities.map(activity => (
              <div key={activity.id} className="activity-item">
                <span className="activity-time">{formatTimestamp(activity.created_at)}</span>
                <span className="activity-text">{activity.description}</span>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  )
}

// Validate props for DashboardContent
DashboardContent.propTypes = {
  stats: PropTypes.object.isRequired,
  activities: PropTypes.array.isRequired,
  formatTimestamp: PropTypes.func.isRequired,
}

// User Administration Content Component
const UserAdministrationContent = ({ currentUser }) => {
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [showUserForm, setShowUserForm] = useState(false)
  const [editingUser, setEditingUser] = useState(null)
  const [isEditing, setIsEditing] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState(null)
  const [deleteModal, setDeleteModal] = useState(false)
  const [licenseModal, setLicenseModal] = useState(false)
  const [userLicenses, setUserLicenses] = useState([])
  const [deleteError, setDeleteError] = useState('')
  const [resetPasswordModal, setResetPasswordModal] = useState(false)
  const [resetPasswordTarget, setResetPasswordTarget] = useState(null)
  const [newPassword, setNewPassword] = useState('')
  const [resetPasswordError, setResetPasswordError] = useState('')
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false)
  const [tokenTarget, setTokenTarget] = useState(null)
  const [userTokens, setUserTokens] = useState([])
  const [tokenError, setTokenError] = useState('')
  const [revokeTokenId, setRevokeTokenId] = useState(null)

  // Load users from API
  const loadUsers = async () => {
    try {
      const response = await apiFetch('/api/admin/users')

      if (response.ok) {
        const data = await response.json()
        setUsers(data.users || [])
      } else {
        setError('Failed to load users')
      }
    } catch (err) {
      setError('Network error while loading users')
      console.error('Error loading users:', err)
    } finally {
      setLoading(false)
    }
  }

  // Load users on component mount
  useEffect(() => {
    void loadUsers()
  }, [])

  const handleShowTokens = async (user) => {
    setTokenTarget(user)
    setUserTokens([])
    setTokenError('')
    setRevokeTokenId(null)
    try {
      const response = await apiFetch(`/api/admin/users/${user.id}/tokens`)
      if (response.ok) {
        const data = await response.json()
        setUserTokens(data.tokens || [])
      } else {
        setTokenError('Tokens konnten nicht geladen werden')
      }
    } catch (err) {
      setTokenError('Netzwerkfehler beim Laden der Tokens')
      console.error('Error loading user tokens:', err)
    }
  }

  const closeTokenModal = () => {
    setTokenTarget(null)
    setUserTokens([])
    setTokenError('')
    setRevokeTokenId(null)
  }

  const confirmRevokeToken = async () => {
    const tokenId = revokeTokenId
    try {
      const response = await apiFetch(`/api/admin/users/${tokenTarget.id}/tokens/${tokenId}`, { method: 'DELETE' })
      if (response.ok) {
        setUserTokens(prev => prev.filter(t => t.id !== tokenId))
        setTokenError('')
      } else {
        setTokenError('Token konnte nicht widerrufen werden')
      }
    } catch (err) {
      setTokenError('Netzwerkfehler beim Widerrufen des Tokens')
      console.error('Error revoking token:', err)
    } finally {
      setRevokeTokenId(null)
    }
  }

  // Handle add new user
  const handleAddUser = () => {
    setEditingUser(null)
    setIsEditing(false)
    setShowUserForm(true)
  }

  // Handle edit user
  const handleEditUser = (user) => {
    setEditingUser(user)
    setIsEditing(true)
    setShowUserForm(true)
  }

  // Handle form save
  const handleFormSave = (savedUser) => {
    if (isEditing) {
      // Update existing user in the list
      setUsers(prevUsers => 
        prevUsers.map(user => 
          user.id === savedUser.id ? savedUser : user
        )
      )
    } else {
      // Add new user to the list
      setUsers(prevUsers => [...prevUsers, savedUser])
    }
    setShowUserForm(false)
    setEditingUser(null)
    setIsEditing(false)
  }

  // Handle form cancel
  const handleFormCancel = () => {
    setShowUserForm(false)
    setEditingUser(null)
    setIsEditing(false)
  }

  // Handle delete user
  const handleDeleteUser = async (user) => {
    setDeleteError('')
    setDeleteTarget(user)
    // Fetch all licenses and filter for this user
    try {
      const res = await apiFetch('/api/admin/licenses')
      const data = await res.json()
      if (res.ok) {
        const licenses = (data.licenses || []).filter(l => l.user && l.user.id === user.id)
        if (licenses.length > 0) {
          setUserLicenses(licenses)
          setLicenseModal(true)
        } else {
          setDeleteModal(true)
        }
      } else {
        setDeleteError(data.error || 'Failed to check user licenses')
      }
    } catch {
      setDeleteError('Network error while checking user licenses')
    }
  }

  // Confirm delete user
  const confirmDeleteUser = async () => {
    if (!deleteTarget) return
    setDeleteError('')
    try {
      const res = await apiFetch(`/api/admin/users/${deleteTarget.id}`, { method: 'DELETE' })
      if (res.ok) {
        setUsers(prev => prev.filter(u => u.id !== deleteTarget.id))
        setDeleteModal(false)
        setDeleteTarget(null)
      } else {
        const data = await res.json()
        setDeleteError(data.error || 'Failed to delete user')
      }
    } catch {
      setDeleteError('Network error while deleting user')
    }
  }

  // Handle password reset user
  const handleResetPassword = (user) => {
    setResetPasswordTarget(user)
    setNewPassword('')
    setResetPasswordError('')
    setResetPasswordModal(true)
  }

  // Confirm password reset
  const confirmResetPassword = async () => {
    if (!resetPasswordTarget || !newPassword) return

    // Validate password complexity
    const passwordError = validatePasswordComplexity(newPassword)
    if (passwordError) {
      setResetPasswordError(passwordError)
      return
    }

    setResetPasswordError('')
    setResetPasswordLoading(true)

    try {
      const res = await apiFetch(`/api/admin/users/${resetPasswordTarget.id}/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ newPassword })
      })

      if (res.ok) {
        setResetPasswordModal(false)
        setResetPasswordTarget(null)
        setNewPassword('')
      } else {
        const data = await res.json()
        setResetPasswordError(data.error || 'Failed to reset password')
      }
    } catch {
      setResetPasswordError('Network error while resetting password')
    } finally {
      setResetPasswordLoading(false)
    }
  }

  // Close password reset modal
  const closeResetPasswordModal = () => {
    setResetPasswordModal(false)
    setResetPasswordTarget(null)
    setNewPassword('')
    setResetPasswordError('')
  }

  if (loading) {
    return (
      <div className="content-section">
        <h2>User Administration</h2>
        <div style={{ textAlign: 'center', padding: '40px', color: '#9ca3af' }}>
          <div className="loading-spinner" style={{ margin: '0 auto 16px' }}></div>
          Loading users...
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="content-section">
        <h2>User Administration</h2>
        <div className="error-message">
          {error}
        </div>
        <button className="btn-primary" onClick={loadUsers}>
          Retry
        </button>
      </div>
    )
  }

  return (
    <div className="content-section">
      <h2>User Administration</h2>
      
      <div className="user-controls">
        <button className="btn-primary" onClick={handleAddUser}>Add New User</button>
        <div className="search-box">
          <input 
            type="text" 
            placeholder="Search users..." 
            className="search-input"
          />
        </div>
      </div>

      <div className="users-table">
        <table>
          <thead>
            <tr>
              <th>ID</th>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 ? (
              <tr>
                <td colSpan="6" style={{ textAlign: 'center', padding: '20px', color: '#9ca3af' }}>
                  No users found
                </td>
              </tr>
            ) : (
              users.map(user => (
                <tr key={user.id}>
                  <td>{user.id}</td>
                  <td>{user.first_name} {user.last_name}</td>
                  <td>{user.email}</td>
                  <td>
                    <span className={`role-badge ${user.role?.name || 'user'}`}>
                      {user.role?.name || 'user'}
                    </span>
                  </td>
                  <td>
                    <span className={`status-badge ${user.is_active ? 'active' : 'pending'}`}>
                      {user.is_active ? 'active' : 'pending'}
                    </span>
                  </td>
                  <td>
                    <button className="btn-edit" onClick={() => handleEditUser(user)}>Edit</button>
                    {currentUser && user.id !== currentUser.id && (
                      <button className="btn-reset" onClick={() => handleResetPassword(user)}>Reset Password</button>
                    )}
                    <button className="btn-edit" onClick={() => handleShowTokens(user)}>Tokens</button>
                    <button className="btn-delete" onClick={() => handleDeleteUser(user)}>Delete</button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showUserForm && (
        <UserForm
          user={editingUser}
          onSave={handleFormSave}
          onCancel={handleFormCancel}
          isEditing={isEditing}
          currentUserId={currentUser?.id}
        />
      )}

      {/* User Tokens Modal */}
      {tokenTarget && (
        <div className="user-form-overlay">
          <div className="user-form-container">
            <div className="user-form-header">
              <h3>Tokens von {tokenTarget.first_name} {tokenTarget.last_name}</h3>
              <button className="close-btn" onClick={closeTokenModal}>×</button>
            </div>
            {tokenError && <div className="error-message">{tokenError}</div>}
            {userTokens.length === 0 && !tokenError ? (
              <p style={{ color: '#9ca3af', marginBottom: '16px' }}>Keine Tokens vorhanden</p>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Prefix</th>
                    <th>Scopes</th>
                    <th>Läuft ab</th>
                    <th>Zuletzt benutzt</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {userTokens.map(token => (
                    <tr key={token.id}>
                      <td>{token.name}</td>
                      <td>{token.token_prefix}</td>
                      <td>{(token.scopes || []).join(', ')}</td>
                      <td>{token.expires_at ? new Date(token.expires_at).toLocaleString() : 'nie'}</td>
                      <td>{token.last_used_at ? new Date(token.last_used_at).toLocaleString() : 'nie'}</td>
                      <td>
                        {revokeTokenId === token.id ? (
                          <>
                            <button type="button" className="btn-delete" onClick={confirmRevokeToken}>Bestätigen</button>
                            <button type="button" className="btn-cancel" onClick={() => setRevokeTokenId(null)}>Abbrechen</button>
                          </>
                        ) : (
                          <button type="button" className="btn-delete" onClick={() => setRevokeTokenId(token.id)}>Widerrufen</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <div className="form-actions">
              <button type="button" className="btn-cancel" onClick={closeTokenModal}>Schließen</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete User Modal */}
      {deleteModal && deleteTarget && (
        <div className="user-form-overlay">
          <div className="user-form-container">
            <div className="user-form-header">
              <h3>Delete User</h3>
              <button className="close-btn" onClick={() => { setDeleteModal(false); setDeleteTarget(null); }}>×</button>
            </div>
            <p style={{ color: '#e5e7eb', marginBottom: '16px' }}>
              Are you sure you want to delete user {deleteTarget.first_name && deleteTarget.last_name ? `"${deleteTarget.first_name} ${deleteTarget.last_name}"` : `"${deleteTarget.email}"`} (ID: {deleteTarget.id})? This action cannot be undone.
            </p>
            {deleteError && <div className="error-message">{deleteError}</div>}
            <div className="form-actions">
              <button type="button" className="btn-cancel" onClick={() => { setDeleteModal(false); setDeleteTarget(null); }}>Cancel</button>
              <button type="button" className="btn-delete" onClick={confirmDeleteUser}>Delete</button>
            </div>
          </div>
        </div>
      )}
      {/* User Has Licenses Modal */}
      {licenseModal && deleteTarget && (
        <div className="user-form-overlay">
          <div className="user-form-container">
            <div className="user-form-header">
              <h3>Cannot Delete User</h3>
              <button className="close-btn" onClick={() => { setLicenseModal(false); setDeleteTarget(null); setUserLicenses([]); }}>×</button>
            </div>
            <p style={{ color: '#e5e7eb', marginBottom: '16px' }}>
              The user "{deleteTarget.first_name} {deleteTarget.last_name}" (ID: {deleteTarget.id}) cannot be deleted because they have the following license(s):
            </p>
            <ul style={{ color: '#fbbf24', marginBottom: '16px' }}>
              {userLicenses.map(lic => (
                <li key={lic.id}>License - Plan: {lic.productPlan ? lic.productPlan.name : lic.productId}, Status: {lic.status}</li>
              ))}
            </ul>
            <div className="form-actions">
              <button type="button" className="btn-cancel" onClick={() => { setLicenseModal(false); setDeleteTarget(null); setUserLicenses([]); }}>Close</button>
            </div>
          </div>
        </div>
      )}
      {/* Reset Password Modal */}
      {resetPasswordModal && resetPasswordTarget && (
        <div className="user-form-overlay">
          <div className="user-form-container">
            <div className="user-form-header">
              <h3>Reset Password</h3>
              <button className="close-btn" onClick={closeResetPasswordModal}>×</button>
            </div>
            <p style={{ color: '#e5e7eb', marginBottom: '16px' }}>
              Reset password for user {resetPasswordTarget.first_name && resetPasswordTarget.last_name ? `"${resetPasswordTarget.first_name} ${resetPasswordTarget.last_name}"` : `"${resetPasswordTarget.email}"`} (ID: {resetPasswordTarget.id})
            </p>
            <div className="form-group">
              <label htmlFor="newPassword">New Password</label>
              <input
                type="password"
                id="newPassword"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Min 12 chars, uppercase, lowercase, digit, special char"
                minLength="12"
                required
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  backgroundColor: '#374151',
                  border: '1px solid #4b5563',
                  borderRadius: '4px',
                  color: '#e5e7eb',
                  fontSize: '14px'
                }}
              />
            </div>
            {resetPasswordError && <div className="error-message">{resetPasswordError}</div>}
            <div className="form-actions">
              <button type="button" className="btn-cancel" onClick={closeResetPasswordModal} disabled={resetPasswordLoading}>Cancel</button>
              <button
                type="button"
                className="btn-primary"
                onClick={confirmResetPassword}
                disabled={resetPasswordLoading || newPassword.length < 12}
              >
                {resetPasswordLoading ? 'Resetting...' : 'Reset Password'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

UserAdministrationContent.propTypes = {
  currentUser: PropTypes.shape({
    id: PropTypes.number.isRequired
  }).isRequired
}

// Analytics Content Component
const AnalyticsContent = () => {
  const [deviceAnalytics, setDeviceAnalytics] = useState({ mobile_devices: [], desktop_devices: [] })
  const [appAnalytics, setAppAnalytics] = useState({ apps: [] })
  const [osVersionAnalytics, setOsVersionAnalytics] = useState({ ios_versions: [], macos_versions: [] })
  const [androidVersionAnalytics, setAndroidVersionAnalytics] = useState({ android_versions: [] })
  const [languageAnalytics, setLanguageAnalytics] = useState({ languages: [] })
  const [softwareVersionAnalytics, setSoftwareVersionAnalytics] = useState({ versions: [] })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [windowsAnalytics, setWindowsAnalytics] = useState({ windows_versions: [], windows_form_factors: [], windows_architectures: [], windows_models: [] })
  const [windowsError, setWindowsError] = useState('')
  const [selectedSoftware, setSelectedSoftware] = useState('')
  const [selectedSoftwareForDevices, setSelectedSoftwareForDevices] = useState('')
  const [deviceDistributionData, setDeviceDistributionData] = useState(null)

  // State for iPhone model analytics
  const [iPhoneModels, setIPhoneModels] = useState([])
  const [loadingIPhones, setLoadingIPhones] = useState(true)
  const [iPhoneError, setIPhoneError] = useState('')

  // State for iPad model analytics
  const [iPadModels, setIPadModels] = useState([])
  const [loadingIPads, setLoadingIPads] = useState(true)
  const [iPadError, setIPadError] = useState('')

  // State for Android Phone model analytics
  const [androidPhoneModels, setAndroidPhoneModels] = useState([])
  const [loadingAndroidPhones, setLoadingAndroidPhones] = useState(true)
  const [androidPhoneError, setAndroidPhoneError] = useState('')

  // State for Android Tablet model analytics
  const [androidTabletModels, setAndroidTabletModels] = useState([])
  const [loadingAndroidTablets, setLoadingAndroidTablets] = useState(true)
  const [androidTabletError, setAndroidTabletError] = useState('')

  useEffect(() => {
    const processBasicAnalytics = (responses, data) => {
      const [deviceRes, appRes, osVersionRes, androidVersionRes, languageRes] = responses
      const [deviceData, appData, osVersionData, androidVersionData, languageData] = data

      if (deviceRes.ok) setDeviceAnalytics(deviceData)
      if (appRes.ok) setAppAnalytics(appData)
      if (osVersionRes.ok) setOsVersionAnalytics(osVersionData)
      if (androidVersionRes.ok) setAndroidVersionAnalytics(androidVersionData)
      if (languageRes.ok) setLanguageAnalytics(languageData)
    }

    const processSoftwareAnalytics = (softwareVersionRes, softwareVersionData) => {
      if (!softwareVersionRes.ok) return

      setSoftwareVersionAnalytics(softwareVersionData)
      const versions = softwareVersionData.versions
      if (!versions || versions.length === 0) return

      const uniqueSoftware = [...new Set(versions.map(v => v.software_name))].sort((a, b) => a.localeCompare(b))
      if (uniqueSoftware.length > 0) {
        setSelectedSoftware(uniqueSoftware[0])
        setSelectedSoftwareForDevices(uniqueSoftware[0])
      }
    }

    const processDeviceModelAnalytics = (deviceData) => {
      const { iPhoneRes, iPhoneData, iPadRes, iPadData, androidPhoneRes, androidPhoneData, androidTabletRes, androidTabletData } = deviceData
      setIPhoneModels(iPhoneRes.ok ? (iPhoneData.iphone_models || []) : [])
      setIPhoneError(iPhoneRes.ok ? '' : 'Failed to load iPhone model analytics')
      setLoadingIPhones(false)

      setIPadModels(iPadRes.ok ? (iPadData.ipad_models || []) : [])
      setIPadError(iPadRes.ok ? '' : 'Failed to load iPad model analytics')
      setLoadingIPads(false)

      setAndroidPhoneModels(androidPhoneRes.ok ? (androidPhoneData.android_phone_models || []) : [])
      setAndroidPhoneError(androidPhoneRes.ok ? '' : 'Failed to load Android Phone model analytics')
      setLoadingAndroidPhones(false)

      setAndroidTabletModels(androidTabletRes.ok ? (androidTabletData.android_tablet_models || []) : [])
      setAndroidTabletError(androidTabletRes.ok ? '' : 'Failed to load Android Tablet model analytics')
      setLoadingAndroidTablets(false)
    }

    const processWindowsAnalytics = async (windowsRes) => {
      try {
        if (!windowsRes.ok) throw new Error('windows analytics request failed')
        const data = await windowsRes.json()
        setWindowsAnalytics({
          windows_versions: data.windows_versions || [],
          windows_form_factors: data.windows_form_factors || [],
          windows_architectures: data.windows_architectures || [],
          windows_models: data.windows_models || []
        })
        setWindowsError('')
      } catch (err) {
        console.error('Error loading Windows analytics:', err)
        setWindowsError('Failed to load Windows analytics')
      }
    }

    const fetchAnalyticsData = async () => {
      setLoading(true)
      setError('')
      setWindowsError('')
      try {
        const [deviceRes, appRes, osVersionRes, androidVersionRes, languageRes, softwareVersionRes, iPhoneRes, iPadRes, androidPhoneRes, androidTabletRes, windowsRes] = await Promise.all([
          apiFetch('/api/admin/device-analytics'),
          apiFetch('/api/admin/app-analytics'),
          apiFetch('/api/admin/os-version-analytics'),
          apiFetch('/api/admin/android-version-analytics'),
          apiFetch('/api/admin/language-analytics'),
          apiFetch('/api/admin/software-version-analytics'),
          apiFetch('/api/admin/iphone-model-analytics'),
          apiFetch('/api/admin/ipad-model-analytics'),
          apiFetch('/api/admin/android-phone-model-analytics'),
          apiFetch('/api/admin/android-tablet-model-analytics'),
          apiFetch('/api/admin/windows-analytics')
        ])

        // Runs alongside the other body reads; it handles its own errors and never rejects.
        const windowsDone = processWindowsAnalytics(windowsRes)
        const [deviceData, appData, osVersionData, androidVersionData, languageData, softwareVersionData, iPhoneData, iPadData, androidPhoneData, androidTabletData] = await Promise.all([
          deviceRes.json(),
          appRes.json(),
          osVersionRes.json(),
          androidVersionRes.json(),
          languageRes.json(),
          softwareVersionRes.json(),
          iPhoneRes.json(),
          iPadRes.json(),
          androidPhoneRes.json(),
          androidTabletRes.json()
        ])

        await windowsDone
        processBasicAnalytics([deviceRes, appRes, osVersionRes, androidVersionRes, languageRes], [deviceData, appData, osVersionData, androidVersionData, languageData])
        processSoftwareAnalytics(softwareVersionRes, softwareVersionData)
        processDeviceModelAnalytics({ iPhoneRes, iPhoneData, iPadRes, iPadData, androidPhoneRes, androidPhoneData, androidTabletRes, androidTabletData })

        if (!deviceRes.ok || !appRes.ok || !osVersionRes.ok || !languageRes.ok || !softwareVersionRes.ok) {
          setError('Failed to load some analytics data')
        }
      } catch (err) {
        setError('Network error while loading analytics data')
        console.error('Error loading analytics data:', err)
      } finally {
        setLoading(false)
      }
    }

    void fetchAnalyticsData()
  }, [])

  // Fetch device distribution data when software selection changes
  useEffect(() => {
    const fetchDeviceDistribution = async () => {
      if (!selectedSoftwareForDevices) {
        setDeviceDistributionData(null)
        return
      }

      try {
        const response = await apiFetch(`/api/admin/software-device-distribution?app_name=${encodeURIComponent(selectedSoftwareForDevices)}`)
        if (response.ok) {
          const data = await response.json()
          setDeviceDistributionData(data)
        } else {
          console.error('Failed to fetch device distribution data')
          setDeviceDistributionData(null)
        }
      } catch (error) {
        console.error('Error fetching device distribution data:', error)
        setDeviceDistributionData(null)
      }
    }

    void fetchDeviceDistribution()
  }, [selectedSoftwareForDevices])

  const createMobileDeviceChart = () => {
    const mobileDevices = deviceAnalytics.mobile_devices || []

    if (mobileDevices.length === 0) return { labels: [], datasets: [{ data: [] }] }

    const labels = mobileDevices.map(device => device.device_model)
    const data = mobileDevices.map(device => device.count)

    return {
      labels: labels,
      datasets: [{
        data: data,
        backgroundColor: [
          '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0',
          '#9966FF', '#FF9F40', '#FF6384', '#C9CBCF'
        ],
        borderWidth: 2,
        borderColor: '#fff'
      }]
    }
  }

  const createDesktopDeviceChart = () => {
    const desktopDevices = deviceAnalytics.desktop_devices || []

    if (desktopDevices.length === 0) return { labels: [], datasets: [{ data: [] }] }

    const labels = desktopDevices.map(device => device.device_model)
    const data = desktopDevices.map(device => device.count)

    return {
      labels: labels,
      datasets: [{
        data: data,
        backgroundColor: [
          '#36A2EB', '#FF6384', '#FFCE56', '#4BC0C0',
          '#9966FF', '#FF9F40', '#FF6384', '#C9CBCF'
        ],
        borderWidth: 2,
        borderColor: '#fff'
      }]
    }
  }

  const createAppUsageChart = () => {
    const apps = appAnalytics.apps || []

    if (apps.length === 0) return { labels: [], datasets: [{ data: [] }] }

    const labels = apps.map(app => app.app_name)
    const data = apps.map(app => app.count)

    return {
      labels: labels,
      datasets: [{
        data: data,
        backgroundColor: [
          '#FFCE56', '#FF6384', '#36A2EB', '#4BC0C0',
          '#9966FF', '#FF9F40', '#FF6384', '#C9CBCF'
        ],
        borderWidth: 2,
        borderColor: '#fff'
      }]
    }
  }

  const createIOSVersionChart = () => {
    const iosVersions = osVersionAnalytics.ios_versions || []

    if (iosVersions.length === 0) return { labels: [], datasets: [{ data: [] }] }

    const labels = iosVersions.map(version => version.os_version)
    const data = iosVersions.map(version => version.count)

    return {
      labels: labels,
      datasets: [{
        data: data,
        backgroundColor: [
          '#007AFF', '#5856D6', '#AF52DE', '#FF2D92',
          '#FF3B30', '#FF9500', '#FFCC00', '#34C759'
        ],
        borderWidth: 2,
        borderColor: '#fff'
      }]
    }
  }

  const createMacOSVersionChart = () => {
    const macosVersions = osVersionAnalytics.macos_versions || []

    if (macosVersions.length === 0) return { labels: [], datasets: [{ data: [] }] }

    const labels = macosVersions.map(version => version.os_version)
    const data = macosVersions.map(version => version.count)

    return {
      labels: labels,
      datasets: [{
        data: data,
        backgroundColor: [
          '#1D1D1F', '#86868B', '#F5F5F7', '#0071E3',
          '#6AC4DC', '#30D158', '#FF9F0A', '#FF453A'
        ],
        borderWidth: 2,
        borderColor: '#fff'
      }]
    }
  }

  const createAndroidVersionChart = () => {
    const androidVersions = androidVersionAnalytics.android_versions || []

    if (androidVersions.length === 0) return { labels: [], datasets: [{ data: [] }] }

    const labels = androidVersions.map(version => version.os_version)
    const data = androidVersions.map(version => version.count)

    return {
      labels: labels,
      datasets: [{
        data: data,
        backgroundColor: [
          '#3DDC84', '#A4C639', '#34A853', '#0F9D58',
          '#00897B', '#00695C', '#004D40', '#00BFA5',
          '#1DE9B6', '#64FFDA', '#69F0AE', '#B9F6CA'
        ],
        borderWidth: 2,
        borderColor: '#fff'
      }]
    }
  }

  const createLanguageChart = () => {
    const languages = languageAnalytics.languages || []

    if (languages.length === 0) return { labels: [], datasets: [{ data: [] }] }

    const labels = languages.map(lang => lang.language)
    const data = languages.map(lang => lang.count)

    return {
      labels: labels,
      datasets: [{
        data: data,
        backgroundColor: [
          '#4BC0C0', '#36A2EB', '#FF6384', '#FFCE56',
          '#9966FF', '#FF9F40', '#FF6384', '#C9CBCF',
          '#8DD1E1', '#D4EDDA', '#F8D7DA', '#FCF3CD'
        ],
        borderWidth: 2,
        borderColor: '#fff'
      }]
    }
  }

  const createSoftwareVersionChart = () => {
    const versions = softwareVersionAnalytics.versions || []

    if (versions.length === 0 || !selectedSoftware) {
      return { labels: [], datasets: [{ data: [] }] }
    }

    // Filter versions by selected software
    const filteredVersions = versions.filter(v => v.software_name === selectedSoftware)

    if (filteredVersions.length === 0) {
      return { labels: [], datasets: [{ data: [] }] }
    }

    const labels = filteredVersions.map(v => `${v.software_name} ${v.version} (${v.os_manufacture})`)
    const data = filteredVersions.map(v => v.count)

    return {
      labels: labels,
      datasets: [{
        data: data,
        backgroundColor: [
          '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0',
          '#9966FF', '#FF9F40', '#C9CBCF', '#8DD1E1',
          '#D4EDDA', '#F8D7DA', '#FCF3CD', '#E1F5FE'
        ],
        borderWidth: 2,
        borderColor: '#fff'
      }]
    }
  }

  const getSoftwareOptions = () => {
    const versions = softwareVersionAnalytics.versions || []
    return [...new Set(versions.map(v => v.software_name))].sort((a, b) => a.localeCompare(b))
  }

  const getLegendData = () => {
    const versions = softwareVersionAnalytics.versions || []

    if (versions.length === 0 || !selectedSoftware) {
      return []
    }

    const filteredVersions = versions.filter(v => v.software_name === selectedSoftware)
    return filteredVersions.map(v => ({
      percentage: v.percentage.toFixed(1),
      name: v.software_name,
      version: v.version,
      os_manufacture: v.os_manufacture
    }))
  }

  const createDeviceDistributionChart = () => {
    if (!selectedSoftwareForDevices || !deviceDistributionData) {
      return { labels: [], datasets: [{ data: [] }] }
    }

    const deviceStats = deviceDistributionData.devices || []

    if (deviceStats.length === 0) {
      return { labels: [], datasets: [{ data: [] }] }
    }

    const labels = deviceStats.map(item => item.device_model)
    const data = deviceStats.map(item => item.count)

    return {
      labels: labels,
      datasets: [{
        data: data,
        backgroundColor: [
          '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0',
          '#9966FF', '#FF9F40', '#FF6384', '#C9CBCF',
          '#8DD1E1', '#D4EDDA', '#F8D7DA', '#FCF3CD'
        ],
        borderWidth: 2,
        borderColor: '#fff'
      }]
    }
  }

  const getDeviceLegendData = () => {
    if (!selectedSoftwareForDevices || !deviceDistributionData) {
      return []
    }

    const deviceStats = deviceDistributionData.devices || []

    if (deviceStats.length === 0) {
      return []
    }

    const total = deviceStats.reduce((sum, item) => sum + item.count, 0)
    return deviceStats.map(item => ({
      percentage: ((item.count / total) * 100).toFixed(1),
      name: item.device_model,
      count: item.count
    }))
  }

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          color: '#e5e7eb',
          padding: 20,
          usePointStyle: true,
          font: {
            size: 12
          }
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: '#374151',
        borderWidth: 1,
        callbacks: {
          label: function(context) {
            const label = context.label || ''
            const value = context.parsed
            const total = context.dataset.data.reduce((sum, val) => sum + val, 0)
            const percentage = ((value / total) * 100).toFixed(1)
            return `${label.split(' (')[0]}: ${value} (${percentage}%)`
          }
        }
      },
      datalabels: {
        display: false
      }
    }
  }

  if (loading) {
    return (
      <div className="content-section">
        <h2>Analytics</h2>
        <div style={{ textAlign: 'center', padding: '40px', color: '#9ca3af' }}>
          <div className="loading-spinner" style={{ margin: '0 auto 16px' }}></div>
          Loading analytics data...
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="content-section">
        <h2>Analytics</h2>
        <div className="error-message">{error}</div>
      </div>
    )
  }

  const mobileChartData = createMobileDeviceChart()
  const desktopChartData = createDesktopDeviceChart()
  const appChartData = createAppUsageChart()
  const iosVersionChartData = createIOSVersionChart()
  const createWindowsPieChart = (items, labelKey, colors) => {
    if (items.length === 0) return { labels: [], datasets: [{ data: [] }] }
    return {
      labels: items.map(item => item[labelKey]),
      datasets: [{
        data: items.map(item => item.count),
        backgroundColor: colors,
        borderWidth: 2,
        borderColor: '#fff'
      }]
    }
  }

  const windowsColors = ['#0078D4', '#00BCF2', '#40E0D0', '#8661C5', '#FFB900', '#E74856', '#767676']
  const windowsVersionChartData = createWindowsPieChart(windowsAnalytics.windows_versions, 'os_version', windowsColors)
  const windowsFormFactorChartData = createWindowsPieChart(windowsAnalytics.windows_form_factors, 'form_factor', windowsColors)
  const windowsArchChartData = createWindowsPieChart(windowsAnalytics.windows_architectures, 'arch', windowsColors)

  const macosVersionChartData = createMacOSVersionChart()
  const androidVersionChartData = createAndroidVersionChart()
  const languageChartData = createLanguageChart()
  const softwareVersionChartData = createSoftwareVersionChart()

  return (
    <div className="content-section">
      <h2>Analytics</h2>

      {/* Row 1: Software Version Distribution - Device Distribution for Software */}
      <div style={{
        display: 'flex',
        gap: '24px',
        marginBottom: '24px'
      }}>
        {/* Software Version Distribution Chart */}
        <div style={{
          display: 'flex',
          width: '50%',
          backgroundColor: '#1f2937',
          padding: '20px',
          borderRadius: '8px',
          border: '1px solid #374151'
        }}>
        {/* Left side - Pie chart */}
        <div style={{ flex: '1', marginRight: '20px' }}>
          <h3 style={{
            color: '#e5e7eb',
            textAlign: 'center',
            marginBottom: '16px',
            fontSize: '16px',
            fontWeight: '600'
          }}>
            Software Version Distribution
          </h3>
          <div style={{ height: '300px' }}>
            {softwareVersionChartData.datasets[0].data.length > 0 ? (
              <Pie
                data={softwareVersionChartData}
                options={{
                  ...chartOptions,
                  plugins: {
                    ...chartOptions.plugins,
                    legend: {
                      display: false
                    }
                  }
                }}
              />
            ) : (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                color: '#9ca3af',
                fontStyle: 'italic',
                fontSize: '18px'
              }}>
                No data available
              </div>
            )}
          </div>
        </div>

        {/* Right side - Combobox and legend */}
        <div style={{ flex: '1' }}>
          <div style={{ marginBottom: '20px' }}>
            <label htmlFor="software-version-select" style={{
              display: 'block',
              color: '#e5e7eb',
              marginBottom: '8px',
              fontSize: '14px',
              fontWeight: '500'
            }}>
              Select Software:
            </label>
            <select
              id="software-version-select"
              value={selectedSoftware}
              onChange={(e) => setSelectedSoftware(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                backgroundColor: '#374151',
                color: '#e5e7eb',
                border: '1px solid #4b5563',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            >
              {getSoftwareOptions().map(software => (
                <option key={software} value={software}>
                  {software}
                </option>
              ))}
            </select>
          </div>

          {/* Legend */}
          <div>
            <h4 style={{
              color: '#e5e7eb',
              marginBottom: '12px',
              fontSize: '14px',
              fontWeight: '500'
            }}>
              Legend:
            </h4>
            {getLegendData().length > 0 ? (
              <div style={{ fontSize: '12px', color: '#d1d5db' }}>
                {getLegendData().map((item) => (
                  <div key={`${item.name}-${item.version}-${item.os_manufacture}`} style={{ marginBottom: '6px' }}>
                    {item.percentage}% - {item.name} {item.version} ({item.os_manufacture})
                  </div>
                ))}
              </div>
            ) : (
              <div style={{
                color: '#9ca3af',
                fontStyle: 'italic',
                fontSize: '12px'
              }}>
                No data available
              </div>
            )}
          </div>
        </div>
        </div>

        {/* Device Distribution Chart */}
        <div style={{
          display: 'flex',
          width: '50%',
          backgroundColor: '#1f2937',
          padding: '20px',
          borderRadius: '8px',
          border: '1px solid #374151'
        }}>
          {/* Left side - Pie chart */}
          <div style={{ flex: '1', marginRight: '20px' }}>
            <h3 style={{
              color: '#e5e7eb',
              textAlign: 'center',
              marginBottom: '16px',
              fontSize: '16px',
              fontWeight: '600'
            }}>
              Device Distribution for Software
            </h3>
            <div style={{ height: '300px' }}>
              {createDeviceDistributionChart().datasets[0].data.length > 0 ? (
                <Pie
                  data={createDeviceDistributionChart()}
                  options={{
                    ...chartOptions,
                    plugins: {
                      ...chartOptions.plugins,
                      legend: {
                        display: false
                      }
                    }
                  }}
                />
              ) : (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  color: '#9ca3af',
                  fontStyle: 'italic',
                  fontSize: '18px'
                }}>
                  No data available
                </div>
              )}
            </div>
          </div>

          {/* Right side - Combobox and legend */}
          <div style={{ flex: '1' }}>
            <div style={{ marginBottom: '20px' }}>
              <label htmlFor="software-selector" style={{
                display: 'block',
                color: '#e5e7eb',
                marginBottom: '8px',
                fontSize: '14px',
                fontWeight: '500'
              }}>
                Select Software:
              </label>
              <select
                id="software-selector"
                value={selectedSoftwareForDevices}
                onChange={(e) => setSelectedSoftwareForDevices(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  backgroundColor: '#374151',
                  color: '#e5e7eb',
                  border: '1px solid #4b5563',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              >
                <option value="">Select a software...</option>
                {[...new Set((softwareVersionAnalytics.versions || []).map(v => v.software_name))].sort((a, b) => a.localeCompare(b)).map(softwareName => (
                  <option key={softwareName} value={softwareName}>
                    {softwareName}
                  </option>
                ))}
              </select>
            </div>

            {/* Legend */}
            <div>
              <h4 style={{
                color: '#e5e7eb',
                marginBottom: '12px',
                fontSize: '14px',
                fontWeight: '500'
              }}>
                Legend:
              </h4>
              {getDeviceLegendData().length > 0 ? (
                <div style={{ fontSize: '12px', color: '#d1d5db' }}>
                  {getDeviceLegendData().map((item) => (
                    <div key={item.name} style={{ marginBottom: '6px' }}>
                      {item.percentage}% - {item.name}
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{
                  color: '#9ca3af',
                  fontStyle: 'italic',
                  fontSize: '12px'
                }}>
                  No data available
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Row 2: Top 10 iPhone Models - Top 10 iPad Models */}
      <div style={{
        display: 'flex',
        gap: '24px',
        marginBottom: '24px'
      }}>
        <DeviceModelChart
          title="Top 10 iPhone Models"
          models={iPhoneModels}
          loading={loadingIPhones}
          error={iPhoneError}
          backgroundColor="#3b82f6"
          borderColor="#1d4ed8"
          emptyMessage="No iPhone data available"
        />

        <DeviceModelChart
          title="Top 10 iPad Models"
          models={iPadModels}
          loading={loadingIPads}
          error={iPadError}
          backgroundColor="#10b981"
          borderColor="#059669"
          emptyMessage="No iPad data available"
        />
      </div>

      {/* Row 2b: Top 10 Android Phone Models - Top 10 Android Tablet Models */}
      <div style={{
        display: 'flex',
        gap: '24px',
        marginBottom: '24px'
      }}>
        <DeviceModelChart
          title="Top 10 Android Phone Models"
          models={androidPhoneModels}
          loading={loadingAndroidPhones}
          error={androidPhoneError}
          backgroundColor="#3b82f6"
          borderColor="#1d4ed8"
          emptyMessage="No Android Phone data available"
        />

        <DeviceModelChart
          title="Top 10 Android Tablet Models"
          models={androidTabletModels}
          loading={loadingAndroidTablets}
          error={androidTabletError}
          backgroundColor="#10b981"
          borderColor="#059669"
          emptyMessage="No Android Tablet data available"
        />
      </div>

      {/* Row 3: Mobile Device Usage - Desktop Device Usage - App Usage */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '24px', marginBottom: '24px' }}>
        <PieChartCard title="Mobile Device Usage" chartData={mobileChartData} chartOptions={chartOptions} emptyMessage="No mobile devices found" />
        <PieChartCard title="Desktop Device Usage" chartData={desktopChartData} chartOptions={chartOptions} emptyMessage="No desktop devices found" />
        <PieChartCard title="App Usage" chartData={appChartData} chartOptions={chartOptions} emptyMessage="No app licenses found" />
      </div>

      {/* Row 4: iOS Version Distribution - macOS Version Distribution - Android Version Distribution - Language Distribution */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '24px', marginBottom: '24px' }}>
        <PieChartCard title="iOS Version Distribution" chartData={iosVersionChartData} chartOptions={chartOptions} emptyMessage="No iOS devices found" />
        <PieChartCard title="macOS Version Distribution" chartData={macosVersionChartData} chartOptions={chartOptions} emptyMessage="No macOS devices found" />
        <PieChartCard title="Android Version Distribution" chartData={androidVersionChartData} chartOptions={chartOptions} emptyMessage="No Android devices found" />
        <PieChartCard title="Language Distribution" chartData={languageChartData} chartOptions={chartOptions} emptyMessage="No language data found" />
      </div>

      {/* Row 5: Windows Version Distribution - Windows Laptop vs Desktop - Windows x64 vs ARM64 - Top 10 Windows Models */}
      {windowsError ? (
        <div className="error-message" style={{ marginBottom: '24px' }}>{windowsError}</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '24px', marginBottom: '24px' }}>
          <PieChartCard title="Windows Version Distribution" chartData={windowsVersionChartData} chartOptions={chartOptions} emptyMessage="No Windows devices found" />
          <PieChartCard title="Windows: Laptop vs Desktop" chartData={windowsFormFactorChartData} chartOptions={chartOptions} emptyMessage="No Windows devices found" />
          <PieChartCard title="Windows: x64 vs ARM64" chartData={windowsArchChartData} chartOptions={chartOptions} emptyMessage="No Windows devices found" />
          <DeviceModelChart
            title="Top 10 Windows Models"
            models={windowsAnalytics.windows_models}
            loading={false}
            error=""
            backgroundColor="#0078D4"
            borderColor="#005A9E"
            emptyMessage="No Windows model data available"
          />
        </div>
      )}

      {/* Add spacing at the end */}
      <div style={{ marginBottom: '40px' }}></div>
    </div>
  )
}

export default AdminDashboard

// Validate props for AdminDashboard
AdminDashboard.propTypes = {
  user: PropTypes.shape({
    first_name: PropTypes.string.isRequired,
    last_name: PropTypes.string.isRequired,
  }).isRequired,
  onLogout: PropTypes.func.isRequired,
} 
